import { memo, useEffect, useState } from "react";
import MolecularStructure from "@/components/charts/MolecularStructure";
import {
  SHOW_AI_NOTE,
  SHOW_JSD_LABEL,
  SHOW_MATCH_PERCENTAGE,
} from "@/config/displayPolicy";
import type { Assessment } from "@/types";
import type { FeatureData } from "@/types/spectrum";
import {
  ASSESSMENT_STATUS_LABEL,
  assessmentBadgeClass,
} from "@/utils/assessment";
import { getKoreanName } from "@/utils/compoundKoreanName";
import { formatRt } from "@/utils/formatRt";
import { panelCls } from "@/utils/panelCls";
import { vpFieldLabel, vpTooltip, vpValueText } from "@/utils/vaporPressure";

interface FeatureDetailPanelProps {
  feature: FeatureData | null;
  onClose: () => void;
  /**
   * iter2 B-2: feature=null 일 때 프리뷰할 후보 — `jsd_distance` 최솟값.
   * page 가 derive 해서 전달. panel 은 표시만 담당.
   *
   * 2026-08-28 (P2-3): 옛 이름 `recommendedFeature`. "추천"은 우리가 붙인
   * 해석이고 이 값은 거리 최솟값일 뿐이라 이름·문구를 사실대로 바꿨다.
   */
  lowestJsdFeature?: FeatureData | null;
  /**
   * iter2 B-2: 그 후보의 "상세 보기" 클릭 시 호출.
   */
  onSelectLowestJsd?: () => void;
  /**
   * T1: 같은 스캔(관찰)에서 나온 다른 AI 후보들. page 가 derive 해서 전달.
   * 후보끼리는 같은 스펙트럼의 대안이라 참조 순위가 같고 독립 검출이 아니다.
   */
  siblings?: FeatureData[];
  /** T1: 형제 후보 클릭 → 그 후보로 선택 이동. */
  onSelectSibling?: (featureId: string) => void;
  /** T2-3: 이 후보의 현재(최신) 판정 — 없으면 null(판정 입력 전). */
  assessment?: Assessment | null;
  /** T2-3: 판정 이력 건수. */
  assessmentCount?: number;
  /** T2-3: 판정 기록 권한 (분석관 이상). */
  canAssess?: boolean;
  /** T2-3: 판정 제출 — (status, rationale). 사람 입력만 기록된다. */
  onSubmitAssessment?: (status: string, rationale: string) => void;
  /** T2-3: 제출 중 표시. */
  assessmentSubmitting?: boolean;
  /** T2-3: 제출 실패 메시지 — 입력은 보존된다. */
  assessmentError?: string | null;
}

/**
 * T2-3 판정 카드 — 현재 판정 표시 + (권한 시) 입력 폼.
 * 판정은 감정인의 판단 기록이며 화면이 자동으로 만들지 않는다.
 * result_id 없는(목업) 후보에는 그리지 않는다.
 */
function AssessmentCard({
  assessment,
  assessmentCount = 0,
  canAssess = false,
  onSubmitAssessment,
  submitting = false,
  submitError = null,
}: {
  assessment: Assessment | null | undefined;
  assessmentCount?: number;
  canAssess?: boolean;
  onSubmitAssessment?: (status: string, rationale: string) => void;
  submitting?: boolean;
  submitError?: string | null;
}) {
  const [status, setStatus] = useState("");
  const [rationale, setRationale] = useState("");
  const canSubmit = status !== "" && rationale.trim().length > 0 && !submitting;

  // 리뷰 #12: 입력은 성공이 확인된 뒤에만 비운다 — 새 판정(assessment.id)이
  // 도착하면 성공. 실패하면 감정인의 근거 텍스트를 잃지 않는다.
  const latestId = assessment?.id;
  useEffect(() => {
    setStatus("");
    setRationale("");
  }, [latestId]);

  return (
    <div
      data-testid="assessment-card"
      className="mb-3 rounded border border-gray-200 bg-white p-2"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          판정 (감정인)
        </span>
        {assessmentCount > 0 && (
          <span className="text-[10px] text-gray-400">
            판정 이력 {assessmentCount}건
          </span>
        )}
      </div>
      {assessment ? (
        <div className="text-xs">
          <span
            className={`inline-block rounded px-1.5 py-0.5 font-medium ${assessmentBadgeClass(assessment.status)}`}
          >
            {ASSESSMENT_STATUS_LABEL[assessment.status] ?? assessment.status}
          </span>
          <p className="mt-1 text-gray-700">{assessment.rationale}</p>
          <p className="mt-0.5 text-[10px] text-gray-400">
            {assessment.assessed_by_username ?? "-"}
            {assessment.created_at &&
              ` · ${new Date(assessment.created_at).toLocaleString("ko-KR")}`}
          </p>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          판정 입력 전 — 화면은 판정을 자동으로 만들지 않습니다.
        </p>
      )}
      {canAssess && onSubmitAssessment && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <label
            htmlFor="assessment-status"
            className="block text-[11px] font-medium text-gray-600"
          >
            판정 상태
          </label>
          <select
            id="assessment-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-0.5 w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
          >
            <option value="">선택…</option>
            {Object.entries(ASSESSMENT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <label
            htmlFor="assessment-rationale"
            className="mt-1.5 block text-[11px] font-medium text-gray-600"
          >
            판단 근거
          </label>
          <textarea
            id="assessment-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
            placeholder="필수 — 판단의 근거를 기록합니다 (정정은 새 판정으로 남습니다)"
            className="mt-0.5 w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
          />
          {submitError && (
            <p className="mt-1 text-[11px] text-red-600">{submitError}</p>
          )}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmitAssessment(status, rationale.trim())}
            className="mt-1.5 rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white disabled:bg-gray-300"
          >
            {submitting ? "기록 중…" : "판정 기록"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * JSD 거리 → 해석 라벨. 경계값 0.2 / 0.5 는 검증된 기준이 아니라 내부 임시값이라
 * `SHOW_JSD_LABEL` 이 꺼져 있는 동안에는 화면에 나오지 않는다 (2026-08-27).
 * 거리 원값은 그대로 보여준다.
 */
function getJsdLabel(jsd: number): { label: string; cls: string } {
  if (jsd < 0.2) return { label: "신뢰 높음", cls: "text-green-700" };
  if (jsd < 0.5) return { label: "확인 필요", cls: "text-yellow-700" };
  return { label: "매칭 약함", cls: "text-red-700" };
}

// ── P2-1: Shell-fixed inner components ───────────────────────────────────────

/**
 * EmptyContent — feature=null 시 shell 내부에 렌더되는 빈 상태.
 * DOM 구조 교체 없이 shell wrapper는 항상 유지됨.
 */
function EmptyContent({
  lowestJsdFeature,
  onSelectLowestJsd,
}: {
  lowestJsdFeature?: FeatureData | null;
  onSelectLowestJsd?: () => void;
}) {
  return (
    <div
      data-testid="feature-detail-panel-empty"
      className="flex flex-col h-full"
    >
      <p className="text-sm text-gray-500 mb-3">
        「AI 동정 후보」 표에서 피크를 선택하면 상세 정보가 표시됩니다.
      </p>
      {lowestJsdFeature && (
        <div className="mt-2 p-3 rounded border border-blue-200 bg-blue-50">
          {/* 2026-08-28 (P2-3): 옛 문구 "추천 — 가장 신뢰도 높은 매칭".
              이 값은 JSD 거리의 최솟값일 뿐이고 '신뢰도'가 아니다 — 경계값
              0.2/0.5 가 우리가 임의로 정한 값이라 2026-08-27 에 해석 라벨을
              보류했는데(SHOW_JSD_LABEL), 같은 근거가 이 문구에도 걸린다.
              사실만 적고, 뽑는 규칙의 한계도 함께 밝힌다. */}
          <p className="text-xs font-semibold text-blue-700 mb-1">
            JSD 거리가 가장 작은 후보
          </p>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-blue-600 bg-white px-1.5 py-0.5 rounded">
              {lowestJsdFeature.feature_id}
            </span>
            <span className="text-sm font-bold text-gray-900">
              {lowestJsdFeature.predicted_substance}
            </span>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            RT {formatRt(lowestJsdFeature.rt)} min
            {SHOW_MATCH_PERCENTAGE &&
              ` / 매칭 ${lowestJsdFeature.matching_score}%`}
            {lowestJsdFeature.jsd_distance != null &&
              ` / JSD ${lowestJsdFeature.jsd_distance.toFixed(3)}`}
          </p>
          {/* 2026-08-28 정정: 옛 문구는 "거리가 비슷한 후보가 여럿이면"이었으나,
              실제 규칙은 부동소수 전 정밀도의 strict `<` 라 **정확히 같을 때만**
              먼저 나온 것이 남는다. 화면은 소수 셋째 자리까지만 보이므로 사람
              눈에 같아 보여도 실제로는 미세한 차이로 갈린다 — 그 사실을 적는다. */}
          <p className="text-[11px] text-gray-500 mb-2 leading-snug">
            거리 순위일 뿐 정답 확률이 아닙니다. 화면에는 소수 셋째 자리까지만
            보이므로, 같아 보이는 후보들도 그보다 작은 차이로 갈립니다(거리가
            정확히 같을 때만 먼저 용출된 스캔의 후보가 남습니다).
          </p>
          <button
            type="button"
            onClick={onSelectLowestJsd}
            disabled={!onSelectLowestJsd}
            className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            이 피크 상세 보기 →
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * DetailContent — feature 선택 시 shell 내부에 렌더되는 상세 뷰.
 */
function DetailContent({
  feature,
  onClose,
  siblings,
  onSelectSibling,
  assessment,
  assessmentCount,
  canAssess,
  onSubmitAssessment,
  assessmentSubmitting,
  assessmentError,
}: {
  feature: FeatureData;
  onClose: () => void;
  siblings?: FeatureData[];
  onSelectSibling?: (featureId: string) => void;
  assessment?: Assessment | null;
  assessmentCount?: number;
  canAssess?: boolean;
  onSubmitAssessment?: (status: string, rationale: string) => void;
  assessmentSubmitting?: boolean;
  assessmentError?: string | null;
}) {
  const jsdInfo =
    SHOW_JSD_LABEL && feature.jsd_distance != null
      ? getJsdLabel(feature.jsd_distance)
      : null;

  return (
    <div data-testid="feature-detail-panel" className="relative h-full">
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="상세 패널 닫기"
        className="absolute top-0 right-0 text-gray-400 hover:text-gray-700 transition-colors"
      >
        <svg
          viewBox="0 0 12 12"
          fill="currentColor"
          className="w-4 h-4"
          aria-hidden="true"
        >
          <path d="M2.22 2.22a.75.75 0 0 1 1.06 0L6 4.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L7.06 6l2.72 2.72a.75.75 0 1 1-1.06 1.06L6 7.06 3.28 9.78a.75.75 0 0 1-1.06-1.06L4.94 6 2.22 3.28a.75.75 0 0 1 0-1.06Z" />
        </svg>
      </button>

      {/* Header — iter2.2 #3: 화합물명 한국어 통용명 + 영문 보조 */}
      <div className="mb-3 pr-6">
        {(() => {
          const { ko, en, hasMapping } = getKoreanName(
            feature.predicted_substance,
          );
          return (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                  {feature.feature_id}
                </span>
                <span className="text-base font-bold text-gray-900">{ko}</span>
              </div>
              {hasMapping && (
                <p className="text-[10px] text-gray-400 mb-1 font-normal">
                  {en}
                </p>
              )}
            </>
          );
        })()}
        <p className="text-xs text-gray-500">
          RT {formatRt(feature.rt)} min &nbsp;/&nbsp; m/z {feature.top_mz}
          {SHOW_MATCH_PERCENTAGE && ` / 매칭 ${feature.matching_score}%`}
          {feature.candidate_rank != null &&
            feature.observation_size != null &&
            feature.observation_size > 1 &&
            ` / 관찰 내 ${feature.candidate_rank}순위 (기록된 후보 ${feature.observation_size}개 중)`}
        </p>
      </div>

      {/* T1: 같은 스캔 후보 — 독립 검출이 아니고 참조 순위가 같다 */}
      {siblings && siblings.length > 0 && (
        <div
          data-testid="same-scan-candidates"
          className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
        >
          <div className="mb-1 font-semibold">
            같은 스캔 후보 {siblings.length}개
          </div>
          <div className="flex flex-wrap gap-1">
            {siblings.map((sibling) => (
              <button
                key={sibling.feature_id}
                type="button"
                onClick={() => onSelectSibling?.(sibling.feature_id)}
                disabled={!onSelectSibling}
                className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[11px] text-amber-900 hover:bg-amber-100 disabled:cursor-default disabled:hover:bg-white"
                title={`${sibling.feature_id} · 관찰 내 ${sibling.candidate_rank ?? "?"}순위`}
              >
                {sibling.predicted_substance}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-amber-800">
            같은 스펙트럼에 대한 대안 후보라 같은 참조물질 순위표를 공유하고,
            서로 독립 검출이 아닙니다.
          </p>
        </div>
      )}

      {/* T2-3: 판정 — result_id 없는(목업) 후보에는 그리지 않는다 */}
      {feature.result_id != null && (
        <AssessmentCard
          assessment={assessment}
          assessmentCount={assessmentCount}
          canAssess={canAssess}
          onSubmitAssessment={onSubmitAssessment}
          submitting={assessmentSubmitting}
          submitError={assessmentError}
        />
      )}

      {/* JSD */}
      {feature.jsd_distance != null && (
        <div className="mb-3 p-2 rounded bg-gray-50 border border-gray-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">
              JSD 거리
              <span className="ml-1 text-[10px] text-gray-400">
                (Jensen-Shannon, 스펙트럼 유사도 — 낮을수록 일치)
              </span>
            </span>
            <span
              className={`font-bold font-mono ${jsdInfo ? jsdInfo.cls : "text-gray-800"}`}
            >
              {feature.jsd_distance.toFixed(3)}
              {jsdInfo && ` — ${jsdInfo.label}`}
            </span>
          </div>
        </div>
      )}

      {/* AI Note */}
      {SHOW_AI_NOTE && feature.ai_note && (
        <p className="text-xs text-gray-600 mb-3 italic">{feature.ai_note}</p>
      )}

      {/* Molecular structure */}
      {feature.smiles && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-700 mb-1">분자 구조</p>
          <MolecularStructure
            smiles={feature.smiles}
            width={180}
            height={120}
            className="mx-auto"
          />
          <p className="text-xs text-center text-gray-400 mt-1 font-mono">
            {feature.smiles}
          </p>
        </div>
      )}

      {/* Physical properties */}
      {(feature.predicted_vp != null ||
        feature.predicted_solubility != null) && (
        <div className="border-t border-gray-200 pt-3 space-y-1">
          <p className="text-xs font-semibold text-purple-800 mb-1">
            KROVEX 물성 예측
          </p>
          {/* 2026-08-28 (P2-4): 값·이름을 vaporPressure 헬퍼로 단일화.
              모바일 인라인 행이 같은 보정값을 '보정' 표기 없이 보여주던
              불일치도 함께 닫힌다. */}
          {feature.predicted_vp != null && (
            <div className="flex justify-between text-xs">
              <span
                className="text-gray-600"
                title={vpTooltip(feature.predicted_vp)}
              >
                {vpFieldLabel()}
              </span>
              <span className="font-mono font-bold text-purple-800">
                {vpValueText(feature.predicted_vp)}
              </span>
            </div>
          )}
          {feature.predicted_solubility != null && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">용해도</span>
              <span className="font-mono font-bold text-purple-800">
                {feature.predicted_solubility.toFixed(2)} log S
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FeatureDetailPanel — desktop col-5 상세 패널 (v2 spec §3, K2).
 *
 * P2-1 DOM 안정화: outer shell wrapper가 항상 동일한 DOM 구조를 유지.
 * feature 유무에 따라 내부 컨텐츠만 swap — early return 제거로 DOM 노드 교체 없음.
 *
 * feature 제공 시: DetailContent 렌더.
 * feature=null + lowestJsdFeature 제공 시 (iter2 B-2): EmptyContent (그 후보 프리뷰 포함).
 * feature=null + 프리뷰 대상 없음 시: EmptyContent (placeholder 안내만).
 */
function FeatureDetailPanel({
  feature,
  onClose,
  lowestJsdFeature,
  onSelectLowestJsd,
  siblings,
  onSelectSibling,
  assessment,
  assessmentCount,
  canAssess,
  onSubmitAssessment,
  assessmentSubmitting,
  assessmentError,
}: FeatureDetailPanelProps) {
  // P2-1: shell은 항상 고정. feature 활성 표시는 내부 컨텐츠 변화로 충분 (accent bar 폐기)
  return (
    <div className={panelCls("muted", "h-full min-h-[280px]")}>
      {feature ? (
        <DetailContent
          feature={feature}
          onClose={onClose}
          siblings={siblings}
          onSelectSibling={onSelectSibling}
          assessment={assessment}
          assessmentCount={assessmentCount}
          canAssess={canAssess}
          onSubmitAssessment={onSubmitAssessment}
          assessmentSubmitting={assessmentSubmitting}
          assessmentError={assessmentError}
        />
      ) : (
        <EmptyContent
          lowestJsdFeature={lowestJsdFeature}
          onSelectLowestJsd={onSelectLowestJsd}
        />
      )}
    </div>
  );
}

export default memo(FeatureDetailPanel);
